/**
 * OpenAI (ChatGPT) Ads measurement pixel, gated on CookieYes consent.
 *
 * The pixel is an advertising tag, so it is never injected - and no event is sent -
 * until the visitor has accepted the CookieYes "advertisement" category. Events
 * measured before that are queued in memory (bounded) and flushed once consent
 * arrives; if it never does they are dropped with the page.
 *
 * CookieYes is delivered by the GTM container (_document.tsx), so it loads after the
 * app and asynchronously. We check `getCkyConsent()` immediately, listen for the
 * CookieYes banner-load / consent-update events, and poll briefly in case the CMP
 * finished loading before our listeners were attached.
 *
 * Only `page_viewed`, `registration_completed` and `checkout_started` are measured from the
 * browser. Paid subscriptions are deliberately NOT: they are sent server-side through the
 * Conversions API, and firing them here too would double count (no shared event_id).
 * `checkout_started` is also sent server-side (google-ads `openai-conversions`, from the TED
 * `checkout_started` tracking event) under the same `event_id`, so OpenAI keeps one.
 *
 * Pixel id: `NEXT_PUBLIC_OPENAI_PIXEL_ID` when set; otherwise the production pixel,
 * but only on the production host so Vercel previews / localhost never report into
 * the live ad account. Disabled inside the Capacitor native apps.
 *
 * Every export is fire-and-forget and never throws.
 */
import { Capacitor } from "@capacitor/core";

const PRODUCTION_HOST = "app.ortex.com";
const PRODUCTION_PIXEL_ID = "Cmim8Z4ivsZhGGH9cdr3Tq";
const PIXEL_SRC = "https://bzrcdn.openai.com/sdk/oaiq.min.js";
const CONSENT_CATEGORY = "advertisement";
const MAX_QUEUE = 20;
const POLL_MS = 500;
const POLL_LIMIT = 30; // ~15s: long enough for GTM -> CookieYes to load

type OaiqFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
    interface Window {
        oaiq?: OaiqFn;
        getCkyConsent?: () => { categories?: Record<string, boolean | undefined> } | undefined;
    }
}

export type OaiqEventOptions = { event_id?: string; custom_event_name?: string };

let injected = false;
let watching = false;
let queue: unknown[][] = [];

const getPixelId = (): string => {
    try {
        if (typeof window === "undefined" || Capacitor.isNativePlatform()) return "";
        const fromEnv = process.env.NEXT_PUBLIC_OPENAI_PIXEL_ID;
        if (fromEnv) return fromEnv;
        return window.location.hostname === PRODUCTION_HOST ? PRODUCTION_PIXEL_ID : "";
    } catch {
        return "";
    }
};

const hasAdConsent = (): boolean => {
    try {
        return window.getCkyConsent?.()?.categories?.[CONSENT_CATEGORY] === true;
    } catch {
        return false;
    }
};

/** Inject OpenAI's loader snippet, init, and flush whatever was queued before consent. */
const inject = () => {
    const pixelId = getPixelId();
    if (injected || !pixelId) return;
    injected = true;
    try {
        (function (w: Window, d: Document, s: string, u: string) {
            if (w.oaiq) return;
            const q: OaiqFn = function (...args: unknown[]) {
                q.q!.push(args);
            };
            q.q = [];
            w.oaiq = q;
            const js = d.createElement(s) as HTMLScriptElement;
            js.async = true;
            js.src = u;
            const f = d.getElementsByTagName(s)[0];
            if (f?.parentNode) f.parentNode.insertBefore(js, f);
            else d.head.appendChild(js);
        })(window, document, "script", PIXEL_SRC);
        window.oaiq!("init", { pixelId });
        const pending = queue;
        queue = [];
        pending.forEach((args) => window.oaiq!(...args));
    } catch {
        /* an ad pixel must never break the app */
    }
};

const onConsentMaybeGranted = () => {
    if (!injected && hasAdConsent()) inject();
};

const watchConsent = () => {
    if (watching) return;
    watching = true;
    document.addEventListener("cookieyes_banner_load", onConsentMaybeGranted);
    document.addEventListener("cookieyes_consent_update", (e: Event) => {
        const accepted = (e as CustomEvent<{ accepted?: string[] }>).detail?.accepted;
        if (Array.isArray(accepted) ? accepted.includes(CONSENT_CATEGORY) : hasAdConsent()) inject();
    });
    onConsentMaybeGranted();
    // Cover a CMP that loaded (and fired its events) before we attached.
    let polls = 0;
    const timer = window.setInterval(() => {
        polls += 1;
        if (injected || typeof window.getCkyConsent === "function" || polls >= POLL_LIMIT) {
            window.clearInterval(timer);
            onConsentMaybeGranted();
        }
    }, POLL_MS);
};

/**
 * `oaiq("measure", ...)` once advertising consent is present; queued until then.
 * After injection, events are dropped if consent has since been withdrawn.
 */
export const oaiqMeasure = (eventName: string, props: Record<string, unknown> = {}, options?: OaiqEventOptions) => {
    try {
        if (!getPixelId()) return;
        const args: unknown[] = options ? ["measure", eventName, props, options] : ["measure", eventName, props];
        if (injected) {
            if (hasAdConsent()) window.oaiq?.(...args);
            return;
        }
        if (queue.length < MAX_QUEUE) queue.push(args);
        watchConsent();
    } catch {
        /* never surface tracking errors */
    }
};

export const oaiqPageView = () => oaiqMeasure("page_viewed", { type: "contents" });

/**
 * The payment form is on screen: OpenAI's standard `checkout_started` event (shape
 * `contents`, amount in minor units). `event_id` is `checkout_<userId>_<yyyymmdd>`, shared
 * with the server-side event, so OpenAI counts one checkout per user per day.
 */
export const oaiqCheckoutStarted = (eventId: string, amount?: number | null, currency?: string | null) => {
    const props: Record<string, unknown> = { type: "contents" };
    if (typeof amount === "number" && Number.isFinite(amount) && currency) {
        props.amount = Math.round(amount * 100);
        props.currency = currency.toUpperCase();
    }
    oaiqMeasure("checkout_started", props, eventId ? { event_id: eventId } : undefined);
};

/**
 * Registration conversion. `event_id` is stable per user (`signup_<userId>`) so OpenAI
 * dedupes a repeat fire, and so a server-side registration event could share it.
 */
export const oaiqRegistrationCompleted = (userId?: number | string | null) => oaiqMeasure("registration_completed", { type: "customer_action" }, userId ? { event_id: `signup_${userId}` } : undefined);
